import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import './tokens.css';

export type ResolvedTheme = 'light' | 'dark';
export type ThemePreference = ResolvedTheme | 'system';

interface ThemeContextValue {
  theme: ResolvedTheme;
  preference: ThemePreference;
  toggleTheme: () => void;
  /** Pick light, dark, or follow the device ("system"). */
  setPreference: (preference: ThemePreference) => void;
  /** Themed element for portals (dialogs, popovers) so they inherit the design tokens. */
  portalContainer: HTMLElement | null;
}

const STORAGE_KEY = 'protega-ds-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  preference: 'system',
  toggleTheme: () => {},
  setPreference: () => {},
  portalContainer: null,
});

export const useTheme = () => useContext(ThemeContext);

const readPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage can be unavailable (private mode); fall back to the system setting.
  }
  return 'system';
};

const systemTheme = (): ResolvedTheme =>
  typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';

interface ThemeProviderProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Applies the design-system tokens to its subtree. Follows the OS light/dark
 * setting until the user picks one explicitly with `toggleTheme`.
 */
const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, className }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme = preference === 'system' ? system : preference;

  // Switch every colour at once: pause CSS transitions (inputs, buttons…) for the frame the theme
  // changes, otherwise elements with `transition-colors` fade in after the rest of the page.
  const firstTheme = useRef(theme);
  useLayoutEffect(() => {
    if (!portalContainer || firstTheme.current === theme) return;
    firstTheme.current = theme;
    portalContainer.setAttribute('data-ds-theme-switching', '');
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => portalContainer.removeAttribute('data-ds-theme-switching'));
    });
    return () => cancelAnimationFrame(frame);
  }, [theme, portalContainer]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A non-persistent choice is fine.
    }
  }, []);

  const toggleTheme = useCallback(() => setPreference(theme === 'dark' ? 'light' : 'dark'), [theme, setPreference]);

  const value = useMemo(
    () => ({ theme, preference, toggleTheme, setPreference, portalContainer }),
    [theme, preference, toggleTheme, setPreference, portalContainer],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div ref={setPortalContainer} data-ds-theme={theme} className={cn('tw-font-sans tw-antialiased', className)}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
